/* =============================================================================
   motion.js — motor de animación del theme
   -----------------------------------------------------------------------------
   Dos librerías, cada una en lo suyo:
   · anime.js v4 (siempre cargado): entradas, letras, contadores, trazos SVG,
     bucles y micro-interacciones. Es lo ligero y lo que más se usa.
   · GSAP + ScrollTrigger (carga BAJO DEMANDA): solo si la página tiene una
     escena atada al scroll ([data-scene]). Sticky, scrub, pin.
   Regla de oro: un elemento lo anima UN solo motor. Lo que lleva data-anim es
   de anime.js; lo que está dentro de una escena [data-scene] es de GSAP.
   Sin guardas de prefers-reduced-motion: Fernanda lo tiene activado y quiere
   ver las animaciones.
   ========================================================================== */
(function () {
  'use strict';

  var ONX = (window.ONX = window.ONX || {});
  ONX.scenes = ONX.scenes || {};          // escenas GSAP registradas por nombre
  ONX.effects = ONX.effects || {};        // efectos anime.js extra por nombre
  var mounted = new WeakMap();            // raíz de sección -> { scope, ctx, io }

  /* ---------------------------------------------------------------- utilidades */
  function num(el, attr, fallback) {
    var v = parseFloat(el.getAttribute(attr));
    return isNaN(v) ? fallback : v;
  }
  function isMobile() { return window.matchMedia('(max-width: 749px)').matches; }

  /* --------------------------------------------------- carga perezosa de GSAP */
  var gsapPromise = null;
  function loadScript(src) {
    return new Promise(function (ok, fail) {
      var s = document.createElement('script');
      s.src = src; s.async = true;
      s.onload = ok; s.onerror = fail;
      document.head.appendChild(s);
    });
  }
  // GSAP ya viene cargado desde el layout (defer). Solo si faltara se carga aquí.
  function listo() {
    window.gsap.registerPlugin(window.ScrollTrigger);
    // las imágenes que cargan tarde mueven la página: recalcular escenas al terminar
    var re = function () { window.ScrollTrigger.refresh(); };
    if (document.readyState === 'complete') setTimeout(re, 200);
    else window.addEventListener('load', function () { setTimeout(re, 200); });
    document.documentElement.classList.add('gsap-ok');
    return window.gsap;
  }
  ONX.gsap = function () {
    if (!gsapPromise) {
      if (window.gsap && window.ScrollTrigger) gsapPromise = Promise.resolve().then(listo);
      else {
        var a = ONX.assets || {};
        gsapPromise = (window.gsap ? Promise.resolve() : loadScript(a.gsap))
          .then(function () { return window.ScrollTrigger ? null : loadScript(a.scrollTrigger); })
          .then(listo)
          .catch(function (e) {
            // sin GSAP: las secciones muestran su estado de reposo (ver .no-gsap en CSS)
            document.documentElement.classList.add('no-gsap');
            throw e;
          });
      }
    }
    return gsapPromise;
  };

  /* ------------------------------------------------------- presets de entrada */
  // Cada preset: estado de partida y llegada. Lo aplica anime.js.
  var PRESETS = {
    'fade':        { from: { opacity: 0 },                          to: { opacity: 1 } },
    'fade-up':     { from: { opacity: 0, y: 32 },                   to: { opacity: 1, y: 0 } },
    'fade-down':   { from: { opacity: 0, y: -32 },                  to: { opacity: 1, y: 0 } },
    'slide-left':  { from: { opacity: 0, x: -64 },                  to: { opacity: 1, x: 0 } },
    'slide-right': { from: { opacity: 0, x: 64 },                   to: { opacity: 1, x: 0 } },
    'zoom':        { from: { opacity: 0, scale: 0.88 },             to: { opacity: 1, scale: 1 } },
    'blur':        { from: { opacity: 0, filter: 'blur(14px)' },    to: { opacity: 1, filter: 'blur(0px)' } },
    'flip':        { from: { opacity: 0, rotateX: -70, y: 20 },     to: { opacity: 1, rotateX: 0, y: 0 } }
  };

  function reveal(el, A) {
    var type = el.getAttribute('data-anim');
    var delay = num(el, 'data-anim-delay', 0);
    var dur = num(el, 'data-anim-dur', 900);
    var ease = el.getAttribute('data-anim-ease') || 'outExpo';

    // ---- texto partido: letras / palabras / líneas
    if (type === 'letters' || type === 'words' || type === 'lines') {
      var split = A.splitText(el, {
        chars: type === 'letters' ? { wrap: 'clip' } : false,
        words: type === 'words' ? { wrap: 'clip' } : (type === 'letters'),
        lines: type === 'lines' ? { wrap: 'clip' } : false
      });
      var parts = type === 'letters' ? split.chars : type === 'words' ? split.words : split.lines;
      var stagger = num(el, 'data-anim-stagger', type === 'letters' ? 32 : 70);
      var style = el.getAttribute('data-anim-style') || 'rise';
      var from = style === 'flip'
        ? { opacity: 0, rotateX: -82, transformOrigin: '50% 100%' }   // persiana 3D (de Pleena)
        : style === 'blur'
          ? { opacity: 0, filter: 'blur(10px)', y: '0.3em' }
          : { y: '105%' };                                            // sube desde abajo, recortado
      if (style === 'flip') el.style.perspective = '760px';
      A.utils.set(parts, from);
      el.style.visibility = 'visible';
      return A.animate(parts, {
        opacity: 1, y: 0, rotateX: 0, filter: 'blur(0px)',
        duration: dur, delay: A.stagger(stagger, { start: delay }), ease: ease
      });
    }

    // ---- contador: 0 -> data-count-to
    if (type === 'count') {
      var to = num(el, 'data-count-to', parseFloat(el.textContent) || 0);
      var dec = num(el, 'data-count-decimals', 0);
      var pre = el.getAttribute('data-count-prefix') || '';
      var suf = el.getAttribute('data-count-suffix') || '';
      var o = { v: 0 };
      var fmt = function () {
        el.textContent = pre + o.v.toLocaleString('es-MX', { minimumFractionDigits: dec, maximumFractionDigits: dec }) + suf;
      };
      fmt();
      return A.animate(o, { v: to, duration: dur * 2, delay: delay, ease: 'outQuart', onUpdate: fmt });
    }

    // ---- trazo SVG que se dibuja
    if (type === 'draw') {
      var paths = el.querySelectorAll('path, circle, line, polyline, polygon, rect, ellipse');
      var drawables = A.svg.createDrawable(paths.length ? paths : el);
      return A.animate(drawables, {
        draw: ['0 0', '0 1'], duration: dur * 1.6, delay: A.stagger(120, { start: delay }), ease: 'inOutQuad'
      });
    }

    // ---- efecto registrado por una sección
    if (ONX.effects[type]) return ONX.effects[type](el, A, { delay: delay, dur: dur, ease: ease });

    // ---- presets simples
    var p = PRESETS[type] || PRESETS['fade-up'];
    if (type === 'flip' && el.parentElement) el.parentElement.style.perspective = '900px';
    A.utils.set(el, p.from);
    var to2 = Object.assign({}, p.to, { duration: dur, delay: delay, ease: ease });
    return A.animate(el, to2);
  }

  // Grupos escalonados: data-anim-group="fade-up" en el padre anima a sus hijos
  function revealGroup(el, A) {
    var kids = Array.prototype.filter.call(el.children, function (c) { return !c.hasAttribute('data-anim'); });
    var p = PRESETS[el.getAttribute('data-anim-group')] || PRESETS['fade-up'];
    var gap = num(el, 'data-anim-stagger', 90);
    A.utils.set(kids, p.from);
    return A.animate(kids, Object.assign({}, p.to, {
      duration: num(el, 'data-anim-dur', 900),
      delay: A.stagger(gap, { start: num(el, 'data-anim-delay', 0) }),
      ease: 'outExpo'
    }));
  }

  /* ------------------------------------------------------------- montar raíz */
  var refreshT;
  function pedirRefresh() { clearTimeout(refreshT); refreshT = setTimeout(function () { if (window.ScrollTrigger) window.ScrollTrigger.refresh(); }, 120); }
  function mount(root) {
    if (!root || mounted.has(root)) return;
    var A = window.anime;
    var state = { scope: null, ctx: null, io: null, cleanups: [] };
    mounted.set(root, state);

    var targets = root.querySelectorAll('[data-anim], [data-anim-group]');
    if (A && targets.length) {
      state.scope = A.createScope({ root: root }).add(function () {
        // estado de partida lo pone JS (si la librería no llega, todo se ve)
        state.io = new IntersectionObserver(function (entries) {
          entries.forEach(function (en) {
            if (!en.isIntersecting) return;
            state.io.unobserve(en.target);
            var el = en.target;
            el.classList.add('is-in');
            if (el.hasAttribute('data-anim-group')) revealGroup(el, A);
            else reveal(el, A);
          });
        }, { rootMargin: '0px 0px -12% 0px', threshold: 0.01 });
        targets.forEach(function (t) { state.io.observe(t); });
      });
    } else {
      targets.forEach(function (t) { t.classList.add('is-in'); });
    }

    // Escenas GSAP: solo cargan la librería si existen
    var scenes = root.querySelectorAll('[data-scene]');
    if (root.hasAttribute('data-scene')) scenes = [root].concat(Array.prototype.slice.call(scenes));
    if (scenes.length) {
      ONX.gsap().then(function (gsap) {
        if (mounted.get(root) !== state) return;    // la sección se desmontó mientras cargaba
        state.ctx = gsap.context(function () {
          Array.prototype.forEach.call(scenes, function (sc) {
            var name = sc.getAttribute('data-scene');
            var fn = ONX.scenes[name];
            if (!fn) { console.warn('[onx] escena sin registrar:', name); return; }
            // cada escena por separado: si una falla, las demás siguen
            try {
              var c = fn(sc, gsap, window.ScrollTrigger, { mobile: isMobile() });
              if (typeof c === 'function') state.cleanups.push(c);
            } catch (err) { console.error('[onx] la escena "' + name + '" falló:', err); }
          });
        }, root);
        pedirRefresh();
      }).catch(function (err) { console.error('[onx] no se pudo iniciar GSAP:', err); });
    }
  }

  function unmount(root) {
    var st = mounted.get(root);
    if (!st) return;
    if (st.io) st.io.disconnect();
    if (st.scope) st.scope.revert();
    if (st.ctx) st.ctx.revert();
    st.cleanups.forEach(function (c) { try { c(); } catch (e) {} });
    mounted.delete(root);
  }

  ONX.mount = mount;
  ONX.unmount = unmount;

  /* ---------------------------------------------------------- escenas base */
  // parallax: data-scene="parallax" data-speed="-20" (en % de su altura)
  ONX.scenes.parallax = function (el, gsap) {
    gsap.fromTo(el, { yPercent: -num(el, 'data-speed', 15) }, {
      yPercent: num(el, 'data-speed', 15), ease: 'none',
      scrollTrigger: { trigger: el.parentElement || el, start: 'top bottom', end: 'bottom top', scrub: 0.6 }
    });
  };

  /* --------------------------------------------- rendimiento: fps reales */
  // Si el teléfono no aguanta, html.lowfx apaga capas decorativas (CSS).
  function measureFps() {
    var frames = 0, start = performance.now();
    function tick(now) {
      frames++;
      if (now - start < 1200) return requestAnimationFrame(tick);
      var fps = frames / ((now - start) / 1000);
      if (fps < 40) document.documentElement.classList.add('lowfx');
      if (fps < 24) document.documentElement.classList.add('minfx');
    }
    requestAnimationFrame(tick);
  }

  /* -------------------------------------------------------------- arranque */
  // Arrancar SOLO cuando todos los scripts con defer ya corrieron: las escenas se
  // registran en pl-v*.js, que cargan después de este archivo. (Un defer corre con
  // readyState 'interactive', ANTES de DOMContentLoaded: arrancar ahí las perdía.)
  var booted = false;
  function boot() {
    if (booted) return; booted = true;
    window.__onxMotion = true;
    document.documentElement.classList.add('motion-ready');
    var raices = Array.prototype.slice.call(document.querySelectorAll('.shopify-section, [data-motion-root]'));
    var lejos = [], tope = window.innerHeight * 2;
    raices.forEach(function (r) { if (r.getBoundingClientRect().top < tope || !('IntersectionObserver' in window)) mount(r); else lejos.push(r); });
    if (lejos.length) {
      var libre = window.requestIdleCallback || function (f) { return setTimeout(f, 1); };
      // se monta con 2 pantallas de anticipación y en un momento libre, para que no caiga en medio del scroll
      var io = new IntersectionObserver(function (en) { en.forEach(function (e) { if (e.isIntersecting) { io.unobserve(e.target); var r = e.target; libre(function () { mount(r); pedirRefresh(); }, { timeout: 400 }); } }); }, { rootMargin: '200% 0px 200% 0px' });
      lejos.forEach(function (r) { io.observe(r); });
      // saltos largos (estrellas → reseñas, banda → packs, #ancla): montar todo antes de calcular a dónde ir
      var todo = function () { io.disconnect(); lejos.forEach(mount); lejos = []; if (window.ScrollTrigger) window.ScrollTrigger.refresh(); };
      document.addEventListener('click', function (e) { if (lejos.length && e.target.closest('[data-scroll-to], a[href^="#"]')) todo(); }, true);
      if (location.hash) todo();
      ONX.mountAll = todo;
    }
    measureFps();
  }
  if (document.readyState === 'complete') boot();
  else {
    document.addEventListener('DOMContentLoaded', boot);
    window.addEventListener('load', boot);
  }

  // Editor de temas: montar / desmontar al reconstruir secciones
  document.addEventListener('shopify:section:load', function (e) { mount(e.target); });
  document.addEventListener('shopify:section:unload', function (e) { unmount(e.target); });
})();
